// =====================================================================
// SOC Mock Data Layer
// =====================================================================
// Every object here is intentionally generic so that later phases can
// map real DSPM / CSPM / Risk Management records onto the same shape
// without redesigning any SOC UI component. Each domain object carries
// a `source: "native" | "dspm" | "cspm" | "risk"` field for that
// purpose - mock data always uses "native".
//
// Future integration interfaces (documented, not implemented):
//
//   DSPM  -> Data Assets, Sensitive Data, DSPM Findings, Data Risks
//   CSPM  -> Cloud Assets, Cloud Findings, Misconfigurations, Cloud Risks
//   Risk  -> Risks, Risk Score, Risk Severity, Risk Treatment, Risk Status
//
// A future adapter only needs to transform those records into the
// Asset/Alert/Incident shapes below and push them into SOCContext.
// =====================================================================

let idCounter = 1000;
export const nextId = (prefix) => `${prefix}-${(idCounter++).toString().padStart(5, "0")}`;

export const SEVERITIES = ["critical", "high", "medium", "low"];

export const ASSET_TYPES = [
  "Server",
  "Workstation",
  "Database",
  "Cloud Resource",
  "Application",
  "Container",
  "Network Device",
];

export const ALERT_STATUSES = ["New", "Acknowledged", "Investigating", "Resolved", "False Positive"];
export const INCIDENT_STATUSES = ["Open", "Investigating", "Contained", "Eradication", "Recovery", "Resolved", "Closed"];
export const INVESTIGATION_STATUSES = ["New", "In Progress", "Waiting", "Completed", "Closed"];
export const RESPONSE_STATUSES = ["Pending", "Running", "Completed", "Failed"];
export const ASSET_STATUSES = ["Online", "Offline", "Warning", "Critical"];

export const RESPONSE_ACTION_TYPES = [
  "Block IP",
  "Disable User",
  "Isolate Asset",
  "Kill Process",
  "Reset Password",
  "Revoke Session",
  "Quarantine File",
  "Create Ticket",
];

export const EVENT_SOURCES = ["Windows", "Linux", "Firewall", "Cloud", "IAM", "Application", "Database", "Endpoint"];

export const EVENT_TYPES = [
  "Login Success",
  "Login Failed",
  "File Modified",
  "File Created",
  "File Deleted",
  "Process Started",
  "Network Connection",
  "Privilege Escalation",
  "Malware Detected",
  "Configuration Changed",
];

export const MITRE_TECHNIQUES = [
  { id: "T1078", name: "Valid Accounts" },
  { id: "T1110", name: "Brute Force" },
  { id: "T1486", name: "Data Encrypted for Impact" },
  { id: "T1059", name: "Command and Scripting Interpreter" },
  { id: "T1021", name: "Remote Services" },
  { id: "T1548", name: "Abuse Elevation Control Mechanism" },
  { id: "T1046", name: "Network Service Discovery" },
];

const ANALYSTS = ["A. Rahman", "S. Devi", "K. Prasad", "M. Iyer", "R. Kapoor"];
const USERS = ["jdoe", "asmith", "rkumar", "mgarcia", "lwong", "svarma", "tnguyen"];
const IPS = ["192.168.1.14", "10.0.4.22", "203.0.113.45", "198.51.100.9", "172.16.5.61", "10.0.9.101"];

const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const hoursAgo = (h) => new Date(Date.now() - h * 3600 * 1000).toISOString();
const minutesAgo = (m) => new Date(Date.now() - m * 60 * 1000).toISOString();

// ---------------------------------------------------------------
// Assets
// ---------------------------------------------------------------
const ASSET_NAME_POOL = [
  ["SRV-001", "Server"], ["SRV-002", "Server"], ["SRV-003", "Server"], ["SRV-004", "Server"], ["SRV-005", "Server"],
  ["WS-CORP-11", "Workstation"], ["WS-CORP-27", "Workstation"], ["WS-CORP-33", "Workstation"], ["WS-CORP-41", "Workstation"],
  ["DB-PROD-01", "Database"], ["DB-PROD-02", "Database"], ["DB-STAGE-01", "Database"],
  ["AWS-EC2-CSPM-3", "Cloud Resource"], ["AZ-VM-CSPM-1", "Cloud Resource"], ["GCP-VM-CSPM-2", "Cloud Resource"], ["AWS-RDS-CSPM-1", "Cloud Resource"],
  ["APP-BILLING", "Application"], ["APP-PORTAL", "Application"], ["APP-CRM", "Application"], ["APP-HRMS", "Application"],
  ["CTR-API-GATEWAY", "Container"], ["CTR-WORKER-02", "Container"], ["CTR-WORKER-03", "Container"], ["CTR-QUEUE-01", "Container"],
  ["FW-EDGE-01", "Network Device"], ["SW-CORE-01", "Network Device"], ["RTR-BRANCH-02", "Network Device"], ["VPN-GATEWAY-01", "Network Device"],
  ["USER-102", "Workstation"], ["USER-118", "Workstation"], ["USER-129", "Workstation"], ["USER-144", "Workstation"],
  ["DSPM-DS-01", "Cloud Resource"], ["CSPM-BUCKET-01", "Cloud Resource"],
];

export const generateAssets = () =>
  ASSET_NAME_POOL.map(([name, type]) => {
    const risk = randInt(5, 95);
    return {
      id: nextId("AST"),
      name,
      type,
      ip: `10.${randInt(0, 255)}.${randInt(0, 255)}.${randInt(2, 250)}`,
      os: type === "Network Device" ? "N/A" : rand(["Windows Server 2022", "Ubuntu 22.04", "RHEL 9", "Windows 11", "Amazon Linux 2"]),
      environment: rand(["Production", "Staging", "Development"]),
      owner: rand(ANALYSTS),
      criticality: risk > 70 ? "Critical" : risk > 45 ? "High" : risk > 20 ? "Medium" : "Low",
      riskScore: risk,
      lastSeen: minutesAgo(randInt(1, 600)),
      status: risk > 80 ? "Critical" : risk > 55 ? "Warning" : rand(["Online", "Online", "Online", "Offline"]),
      source: "native", // future: "dspm" | "cspm"
      sourceRef: null, // future: original DSPM/CSPM record id
    };
  });

// ---------------------------------------------------------------
// Events
// ---------------------------------------------------------------
export const generateEvents = (assets, count = 180) =>
  Array.from({ length: count }).map(() => {
    const asset = rand(assets);
    const type = rand(EVENT_TYPES);
    const severity =
      type === "Malware Detected" || type === "Privilege Escalation"
        ? rand(["critical", "high"])
        : type === "Login Failed" || type === "Configuration Changed"
        ? rand(["medium", "low"])
        : "low";
    return {
      id: nextId("EVT"),
      timestamp: minutesAgo(randInt(1, 2880)),
      eventId: nextId("E"),
      type,
      source: rand(EVENT_SOURCES),
      sourceIp: rand(IPS),
      user: rand(USERS),
      assetId: asset.id,
      assetName: asset.name,
      action: type,
      result: type.includes("Failed") ? "Failure" : "Success",
      severity,
    };
  }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

// ---------------------------------------------------------------
// Alerts
// ---------------------------------------------------------------
const ALERT_TEMPLATES = [
  { name: "Brute Force Attack Detected", severity: "critical", mitre: "T1110" },
  { name: "Unauthorized Login Detected", severity: "critical", mitre: "T1078" },
  { name: "Suspicious Login Location", severity: "high", mitre: "T1078" },
  { name: "Ransomware Behavior Detected", severity: "critical", mitre: "T1486" },
  { name: "Malware Signature Match", severity: "high", mitre: "T1059" },
  { name: "Privilege Escalation Attempt", severity: "high", mitre: "T1548" },
  { name: "Unusual Network Scan", severity: "medium", mitre: "T1046" },
  { name: "File Integrity Violation", severity: "medium", mitre: "T1059" },
  { name: "Multiple Failed Logins", severity: "low", mitre: "T1110" },
  { name: "New Remote Service Access", severity: "low", mitre: "T1021" },
];

export const buildAlert = (assets, events, overrides = {}) => {
  const tmpl = rand(ALERT_TEMPLATES);
  const asset = rand(assets);
  const relatedEvent = rand(events);
  const detectionTime = new Date().toISOString();
  return {
    id: nextId("ALT"),
    name: tmpl.name,
    description: `${tmpl.name} on ${asset.name}. Automated detection flagged anomalous behavior consistent with known attack patterns.`,
    severity: tmpl.severity,
    source: rand(EVENT_SOURCES),
    sourceIp: rand(IPS),
    destination: rand(IPS),
    user: rand(USERS),
    assetId: asset.id,
    assetName: asset.name,
    detectionTime,
    status: "New",
    analyst: null,
    mitre: tmpl.mitre,
    detectionReason: "Behavioral analytics engine matched signature pattern with confidence > 85%.",
    relatedEventId: relatedEvent?.id || null,
    relatedInvestigationId: null,
    relatedIncidentId: null,
    notes: [],
    timeline: [{ time: detectionTime, label: "Alert generated by detection engine" }],
    ...overrides,
  };
};

export const generateInitialAlerts = (assets, events, count = 48) => {
  const alerts = Array.from({ length: count }).map(() => {
    const a = buildAlert(assets, events);
    a.detectionTime = hoursAgo(randInt(1, 96));
    a.timeline[0].time = a.detectionTime;
    a.status = rand(ALERT_STATUSES);
    if (a.status !== "New") a.analyst = rand(ANALYSTS);
    return a;
  });
  return alerts.sort((x, y) => new Date(y.detectionTime) - new Date(x.detectionTime));
};

// ---------------------------------------------------------------
// Incidents
// ---------------------------------------------------------------
const INCIDENT_CATEGORIES = ["Intrusion", "Malware", "Data Exfiltration", "Account Compromise", "Policy Violation"];

export const buildIncidentFromAlert = (alert, overrides = {}) => ({
  id: nextId("INC"),
  name: `Incident: ${alert.name}`,
  severity: alert.severity,
  category: rand(INCIDENT_CATEGORIES),
  source: alert.source,
  affectedAssetId: alert.assetId,
  affectedAssetName: alert.assetName,
  affectedUser: alert.user,
  createdTime: alert.detectionTime,
  analyst: alert.analyst || rand(ANALYSTS),
  status: "Open",
  priority: alert.severity === "critical" ? "P1" : alert.severity === "high" ? "P2" : "P3",
  relatedAlertIds: [alert.id],
  relatedEventIds: alert.relatedEventId ? [alert.relatedEventId] : [],
  affectedAssetIds: [alert.assetId],
  affectedUserIds: [alert.user],
  investigationId: null,
  responseActionIds: [],
  summary: `Incident opened from alert "${alert.name}" detected on ${alert.assetName}.`,
  notes: [],
  resolution: null,
  timeline: [{ time: alert.detectionTime, label: "Incident created from alert" }],
  ...overrides,
});

export const generateInitialIncidents = (alerts) => {
  const critHigh = alerts.filter((a) => a.severity === "critical" || a.severity === "high").slice(0, 16);
  return critHigh.map((a, i) => {
    const inc = buildIncidentFromAlert(a, { status: rand(INCIDENT_STATUSES) });
    a.relatedIncidentId = inc.id;
    return inc;
  });
};

// ---------------------------------------------------------------
// Investigations
// ---------------------------------------------------------------
export const buildInvestigationFromIncident = (incident, alert, overrides = {}) => ({
  id: nextId("INV"),
  title: `Investigation: ${incident.name}`,
  relatedAlertId: alert?.id || null,
  relatedIncidentId: incident.id,
  analyst: incident.analyst,
  priority: incident.priority,
  status: "New",
  startedAt: incident.createdTime,
  lastUpdated: new Date().toISOString(),
  summary: `Investigating root cause for ${incident.name}.`,
  evidence: ["Auth logs", "Endpoint telemetry", "Network flow records"],
  events: incident.relatedEventIds,
  assets: incident.affectedAssetIds,
  users: incident.affectedUserIds,
  ips: alert ? [alert.sourceIp, alert.destination] : [],
  iocs: [],
  notes: [],
  findings: "",
  recommendedActions: [],
  timeline: [{ time: incident.createdTime, label: "Investigation opened" }],
  ...overrides,
});

export const generateInitialInvestigations = (incidents, alerts) =>
  incidents.slice(0, 12).map((inc) => {
    const alert = alerts.find((a) => a.id === inc.relatedAlertIds[0]);
    const inv = buildInvestigationFromIncident(inc, alert, { status: rand(INVESTIGATION_STATUSES) });
    inc.investigationId = inv.id;
    return inv;
  });

// ---------------------------------------------------------------
// Response Actions
// ---------------------------------------------------------------
export const buildResponseAction = (incident, overrides = {}) => ({
  id: nextId("RSP"),
  action: rand(RESPONSE_ACTION_TYPES),
  target: incident?.affectedAssetName || incident?.affectedUser || "N/A",
  triggeredBy: incident?.analyst || "System",
  time: new Date().toISOString(),
  status: "Pending",
  relatedIncidentId: incident?.id || null,
  ...overrides,
});

export const generateInitialResponses = (incidents) =>
  incidents.slice(0, 16).map((inc) =>
    buildResponseAction(inc, {
      status: rand(RESPONSE_STATUSES),
      time: hoursAgo(randInt(1, 72)),
    })
  );

// ---------------------------------------------------------------
// Threat Intelligence
// ---------------------------------------------------------------
const THREAT_ACTORS = ["APT-29", "FIN7", "Lazarus Group", "UNC2452", "Wizard Spider"];
const THREAT_CATEGORIES = ["Ransomware", "Phishing", "C2 Infrastructure", "Credential Theft", "Botnet"];

const IOC_TYPES = ["IP", "Domain", "File Hash"];
const randomIOC = (type) => {
  if (type === "IP") return `${randInt(1, 223)}.${randInt(0, 255)}.${randInt(0, 255)}.${randInt(1, 254)}`;
  if (type === "Domain") return `${rand(["malicious", "phish", "c2", "evil"])}-${randInt(100, 999)}.${rand(["com", "net", "xyz"])}`;
  return Array.from({ length: 32 }, () => "0123456789abcdef"[randInt(0, 15)]).join("");
};

export const generateThreatIntel = (count = 28) =>
  Array.from({ length: count }).map(() => {
    const type = rand(IOC_TYPES);
    return {
      id: nextId("IOC"),
      ioc: randomIOC(type),
      type,
      threat: rand(THREAT_CATEGORIES),
      actor: rand(THREAT_ACTORS),
      confidence: randInt(55, 99),
      firstSeen: hoursAgo(randInt(48, 720)),
      lastSeen: hoursAgo(randInt(0, 47)),
      source: rand(["Internal Detection", "OSINT Feed", "Commercial Feed", "ISAC Sharing"]),
      status: rand(["Active", "Monitoring", "Expired"]),
    };
  });

// ---------------------------------------------------------------
// Reports (definitions only - generation is dummy/frontend-only)
// ---------------------------------------------------------------
export const REPORT_TYPES = [
  "Security Summary",
  "Alert Report",
  "Incident Report",
  "Asset Security Report",
  "Event Log Report",
  "Investigation Report",
  "Response Report",
  "Threat Intelligence Report",
  "Vulnerability Report",
  "SOC Activity Report",
  "Compliance/Security Report",
];

// ---------------------------------------------------------------
// Full initial dataset bundle
// ---------------------------------------------------------------
// Demo/dummy seeding removed - the SOC page now starts from a real
// empty state instead of fabricated assets/alerts/incidents/etc.
// The individual generator functions above (generateAssets,
// buildAlert, buildIncidentFromAlert, buildInvestigationFromIncident,
// buildResponseAction, ...) are kept and still used by real
// interactive flows (escalating an alert to an incident, triggering
// a response action, etc.) - only the "fabricate a starting dataset"
// step is removed. Wiring real data in (backend API / DSPM / CSPM /
// Risk) means populating these same arrays from SOCContext instead
// of leaving them empty.
export const buildInitialDataset = () => ({
  assets: [],
  events: [],
  alerts: [],
  incidents: [],
  investigations: [],
  responses: [],
  threats: [],
});
